import { cva } from 'class-variance-authority'
import Pill from '@/components/ui/Pill'
import Logo from '@/components/ui/Logo'
import Link from '@/components/ui/Link'
import Badge from '@/components/ui/Badge'
import Menu from '@/components/ui/Menu'
import AppStack from '@/components/content/app/AppStack'
import AppIcon from '@/components/content/app/AppIcon'
import DealClaimButton from '@/components/content/deals/DealClaimButton'
import { getDealTags } from '@/components/content/deals/helpers'
import { generateMenu } from '@/helpers/menus'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { cn, getBrandColors, hexToRgba, isPopulatedObject } from '@/lib/utils'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import { createLogger } from '@/lib/logger'
import type { App, Deal, Media } from '@/payload-types'

const logger = createLogger('DealThumbnail')

const block = cva('relative flex', {
  variants: {
    type: {
      thumbnailPrimary:
        'px-6 py-10 bg-gradient-to-t w-full from-white dark:from-white/10 to-zinc-50 dark:to-white/5 rounded-2xl border border-black/5 justify-between items-center shrink-0 md:shrink',
      thumbnailSecondary: 'w-40 md:w-full items-center flex-col-reverse gap-6',
      thumbnailTertiary:
        'relative p-6 w-full rounded-2xl border border-black/5 justify-between items-center shrink-0 xs:shrink',
      list: '',
      categories: '',
    },
  },
})

const icon = cva('relative shrink-0 z-20', {
  variants: {
    type: {
      thumbnailPrimary: 'w-32 h-32 lg:w-[152px] lg:h-[152px]',
      thumbnailSecondary: 'w-40 h-40 lg:w-48 lg:h-48',
      thumbnailTertiary:
        'z-10 w-[120px] h-[120px] rounded-3xl absolute top-8 right-8 [&>span]:absolute [&>span]:h-[120px] [&>span]:w-[120px] [&>span]:rounded-3xl [&>span]:border [&>span]:border-black/5',
      list: '',
      categories: '',
    },
  },
})

type DealThumbnailProps = {
  deal: Deal
  type?: string
  index?: number
  total?: number
  result?: boolean
  placement?: FeaturedPlacement
}

function storeDealID(id: Deal['id'], multipleDeals: boolean) {
  if (multipleDeals) {
    localStorage.setItem('efficient:deal_id', id.toString())
  }
}

export default function DealThumbnail({
  deal,
  type,
  index,
  total,
  result,
  placement,
}: DealThumbnailProps) {
  const app = deal.app as App

  if (!app) return null

  const activeDeals = app.deals?.docs as Deal[]

  const totalDeals = activeDeals?.length ?? 0
  const hasMultipleDeals = totalDeals > 1

  const link = `/deals/${app.slug}`

  const tags = getDealTags(deal)
  const menu = generateMenu('deal', {
    app,
    deals: activeDeals,
    selectedDeal: deal,
    setSelectedDeal: () => {},
  })

  const { secondaryColor, backgroundColor } = getBrandColors(app)

  const isThumbnailPrimary = type === FeaturedTagType.ThumbnailPrimary
  const isThumbnailSecondary = type === FeaturedTagType.ThumbnailSecondary
  const isThumbnailTertiary = type === FeaturedTagType.ThumbnailTertiary

  const appIcon = app.icon as Media
  const appGlyph = app.glyph as Media
  const resolvedIcon = isPopulatedObject<Media>(appGlyph)
    ? appGlyph
    : isPopulatedObject<Media>(appIcon)
      ? appIcon
      : null

  if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
    logger.error('App icon or glyph is not populated in DealThumbnail')
    return null
  }

  if (
    [
      FeaturedTagType.ThumbnailPrimary,
      FeaturedTagType.ThumbnailSecondary,
      FeaturedTagType.ThumbnailTertiary,
    ].includes(type as FeaturedTagType)
  ) {
    return (
      <div
        key={app.id}
        className={cn(block({ type: type as FeaturedTagType }))}
        style={{ zIndex: (total ?? 0) - (index ?? 0) }}
      >
        <Link
          href={link}
          onClick={() => {
            storeDealID(deal.id, hasMultipleDeals)
            if (placement) trackFeaturedClick(placement, app.name)
          }}
          className="absolute inset-0 z-30 rounded-2xl"
          aria-label={`${app.name} Deal`}
        />

        <div className="flex flex-col gap-5">
          <div
            className={cn(
              'flex w-full flex-col',
              isThumbnailPrimary && 'px-1',
              isThumbnailSecondary && 'md:items-center md:text-center',
            )}
          >
            {isThumbnailPrimary && (
              <Badge style="primary" className="mb-2.5">
                New!
              </Badge>
            )}
            {isThumbnailTertiary && (
              <Badge data-review="ignore" className="meticulous-ignore mb-2.5 mt-32 gap-1">
                <span className="font-semibold">{deal.claimed ?? 0}</span>
                claims
              </Badge>
            )}
            <h3 className="my-1 text-20 leading-snug">
              {app.name} Deal{hasMultipleDeals && 's'}
            </h3>
            <p className="line-clamp-1 w-full text-13 text-gray-600">
              {activeDeals?.[0]?.name ?? 'No active deals available'}
              {hasMultipleDeals && (
                <span className={cn('text-gray-400', isThumbnailTertiary && 'block leading-none')}>
                  {!isThumbnailTertiary && ` and ${totalDeals - 1}`} more
                </span>
              )}
            </p>
          </div>

          <div
            className={cn(
              'pointer-events-none relative z-40 flex gap-3 px-0.5 [&>*]:pointer-events-auto',
              isThumbnailSecondary && 'md:justify-center',
            )}
          >
            <DealClaimButton
              deal={deal}
              app={app}
              onClick={() => {
                if (placement) trackFeaturedClick(placement, app.name)
              }}
            >
              <Pill style="primary" className="ml-auto">
                Claim Deal
              </Pill>
            </DealClaimButton>

            {menu && <Menu items={menu} position="up" />}
          </div>
        </div>

        {isThumbnailTertiary ? (
          <div
            className={cn(
              icon({ type }),
              (index ?? 0) % 2 === 1 ? 'rotate-[15deg]' : 'rotate-[-15deg]',
            )}
          >
            <AppStack
              icon={appIcon}
              glyph={appGlyph}
              name={app.name}
              multiple={true}
              background={backgroundColor}
              size="md"
            />
          </div>
        ) : (
          <div className={cn(icon({ type: type as FeaturedTagType }))}>
            <AppIcon
              icon={appIcon}
              glyph={appGlyph}
              name={app.name}
              background={backgroundColor}
              priority={index === 0}
            />
          </div>
        )}

        {isThumbnailTertiary ? (
          <div
            style={{
              background: `linear-gradient(180deg, ${hexToRgba(secondaryColor, 0.15)} 0%, rgba(0,0,0,0) 100%)`,
            }}
            className="absolute left-0 top-0 z-0 h-full w-full overflow-hidden rounded-2xl"
          >
            <div className="absolute bottom-1/2 right-1/2 aspect-square h-full rotate-[15deg] opacity-5">
              <Logo icon={resolvedIcon} alt={app.name} sizes="100vw" fill={true} />
            </div>
          </div>
        ) : isThumbnailPrimary ? (
          <div className="absolute left-0 top-0 z-0 h-full w-full overflow-hidden">
            <div className="absolute right-10 top-1/2 aspect-square h-full -translate-y-1/2 -rotate-[15deg] scale-125 opacity-5">
              <Logo icon={resolvedIcon} alt={app.name} sizes="100vw" fill={true} />
            </div>
          </div>
        ) : null}
      </div>
    )
  } else {
    return (
      <div
        key={app.id}
        className={cn(
          'flex items-center justify-between px-2',
          result && 'rounded-xl border border-gray-200 p-3 dark:border-white/15',
        )}
      >
        <Link
          href={link}
          onClick={() => {
            storeDealID(deal.id, hasMultipleDeals)
            if (placement) trackFeaturedClick(placement, app.name)
          }}
          className="flex w-[calc(100%-90px)] items-center gap-3.5"
        >
          <AppIcon
            icon={appIcon}
            glyph={appGlyph}
            name={app.name}
            background={backgroundColor}
            size={result ? 40 : 56}
          />

          <div className={cn('flex flex-col gap-1', !result && 'mt-1')}>
            <div className="flex h-5 w-full items-center gap-2.5">
              <span
                className={cn(
                  'line-clamp-1 font-semibold text-gray-950 dark:text-white',
                  result ? 'text-14' : 'text-16',
                )}
              >
                {app.name} Deal
              </span>
              {tags.new && <Badge style="primary">New!</Badge>}
              {tags.limited && (
                <Badge style="outline" className="hidden lg:inline-flex">
                  Limited Time!
                </Badge>
              )}
              {tags.daysRemaining && (
                <Badge
                  count={`${tags.daysRemaining}d`}
                  className="text-gray-400"
                  icon="circle-half-dotted-clock"
                />
              )}
            </div>
            <span
              className={cn(
                'line-clamp-1 w-full',
                result
                  ? 'text-12 text-gray-500 dark:text-white/60'
                  : 'text-13 text-gray-600 dark:text-white/50',
              )}
            >
              {deal.name}
            </span>
          </div>
        </Link>

        <div className="flex gap-3">
          <DealClaimButton
            deal={deal}
            app={app}
            onClick={() => {
              if (placement) trackFeaturedClick(placement, app.name)
            }}
          >
            <Pill style="outlinePrimary" className="ml-auto">
              Claim
            </Pill>
          </DealClaimButton>
        </div>
      </div>
    )
  }
}
